/**
 * The task detail panel (Meta's right-hand pane): header bar with Open/Closed,
 * number, owner, priority, status and Save/Cancel; fields; activity & comments.
 * Field edits collect in a draft saved together; stars and comments apply at once.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  BarChart3,
  CalendarDays,
  Clock,
  ExternalLink,
  FolderKanban,
  Github,
  Link2,
  Loader2,
  Lock,
  MoreHorizontal,
  RefreshCw,
  Star,
  Tag,
  Users,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import SearchableSelect from '@/client/components/admin/SearchableSelect';
import {
  type TaskComment,
  type TaskDetail,
  type TaskInput,
  type TaskMeta,
  type TaskPriority,
  type TaskStatus,
  useCommentTaskMutation,
  useCreateTaskMutation,
  useDeleteCommentMutation,
  useEditCommentMutation,
  useGithubSyncMutation,
  useLikeCommentMutation,
  useStarTaskMutation,
  useTaskQuery,
  useUpdateTaskMutation,
} from '@/client/api/tasks/tasks';
import {
  Avatar,
  fmtUpdated,
  personOptions,
  isClosed,
  personName,
  PRIORITY,
  PRIORITY_ORDER,
  STATUS,
  STATUS_ORDER,
  TEXTAREA_CLASS,
} from './taskUi';

interface Draft {
  title: string;
  body: string;
  status: TaskStatus;
  priority: TaskPriority;
  ownerUid: number | null;
  projectId: string | null;
  sectionId: string | null;
  startDate: string;
  dueDate: string;
  progress: number | null;
  estimateHours: string;
  isMilestone: boolean;
  tags: string[];
  subscribers: number[];
  dependsOn: string[]; // T-numbers ("T12") or uuids
  githubIssues: string[];
}

const fromTask = (t: TaskDetail): Draft => ({
  title: t.title,
  body: t.body,
  status: t.status,
  priority: t.priority,
  ownerUid: t.ownerUid,
  projectId: t.projectId,
  sectionId: t.sectionId,
  startDate: t.startDate ?? '',
  dueDate: t.dueDate ?? '',
  progress: t.progress,
  estimateHours: t.estimateHours == null ? '' : String(t.estimateHours),
  isMilestone: t.isMilestone,
  tags: t.tags,
  subscribers: t.subscribers,
  dependsOn: t.blockedBy.map((b) => `T${b.number}`),
  githubIssues: t.githubIssues.map((i) => i.ref),
});

const blank = (defaults: Partial<Draft>, me: number | null): Draft => ({
  title: '',
  body: '',
  status: 'open',
  priority: 'none',
  ownerUid: null,
  projectId: null,
  sectionId: null,
  startDate: '',
  dueDate: '',
  progress: null,
  estimateHours: '',
  isMilestone: false,
  tags: [],
  subscribers: me ? [me] : [],
  dependsOn: [],
  githubIssues: [],
  ...defaults,
});

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** Only the fields that changed, in API shape. */
function diff(before: Draft | null, d: Draft): TaskInput {
  const out: TaskInput = {};
  const set = <K extends keyof Draft>(k: K, value: TaskInput[keyof TaskInput]) => {
    if (!before || !same(before[k], d[k])) (out as Record<string, unknown>)[k] = value;
  };
  set('title', d.title.trim());
  set('body', d.body);
  set('status', d.status);
  set('priority', d.priority);
  set('ownerUid', d.ownerUid);
  set('projectId', d.projectId);
  set('sectionId', d.sectionId);
  set('startDate', d.startDate || null);
  set('dueDate', d.dueDate || null);
  set('progress', d.progress);
  set('estimateHours', d.estimateHours === '' ? null : Number(d.estimateHours));
  set('isMilestone', d.isMilestone);
  set('tags', d.tags);
  set('subscribers', d.subscribers);
  set('dependsOn', d.dependsOn);
  set('githubIssues', d.githubIssues);
  return out;
}

const Row: React.FC<{ icon: React.ReactNode; label: string; children: React.ReactNode }> = ({ icon, label, children }) => (
  <div className="flex min-h-[44px] items-start gap-3 border-b border-gray-100 px-4 py-2.5">
    <div className="flex w-28 shrink-0 items-center gap-2 pt-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
      <span className="text-gray-400">{icon}</span>
      {label}
    </div>
    <div className="min-w-0 flex-1 text-sm">{children}</div>
  </div>
);

const Chip: React.FC<{ children: React.ReactNode; onRemove?: () => void; title?: string }> = ({ children, onRemove, title }) => (
  <span title={title} className="inline-flex max-w-full items-center gap-1 rounded bg-blue-50 px-1.5 py-0.5 text-xs text-blue-700">
    <span className="truncate">{children}</span>
    {onRemove && (
      <button type="button" aria-label="Remove" onClick={onRemove} className="text-blue-400 hover:text-blue-700">
        <X className="h-3 w-3" />
      </button>
    )}
  </span>
);

/** Text input that adds a chip on Enter or comma. */
const ChipInput: React.FC<{ placeholder: string; onAdd: (value: string) => void; ariaLabel: string }> = ({ placeholder, onAdd, ariaLabel }) => {
  const [value, setValue] = useState('');
  const commit = () => {
    const v = value.trim().replace(/,$/, '');
    if (v) onAdd(v);
    setValue('');
  };
  return (
    <input
      aria-label={ariaLabel}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ',') {
          e.preventDefault();
          commit();
        }
      }}
      onBlur={commit}
      placeholder={placeholder}
      className="h-6 min-w-[120px] flex-1 border-0 bg-transparent px-1 text-xs outline-none placeholder:text-gray-400"
    />
  );
};

const SEL = 'h-8 rounded border border-gray-200 bg-white px-2 text-sm';

export interface TaskPanelProps {
  taskId: string | 'new';
  meta: TaskMeta | undefined;
  defaults?: Partial<Draft>;
  onClose: () => void;
  onCreated: (id: string) => void;
}

const TaskPanel: React.FC<TaskPanelProps> = ({ taskId, meta, defaults, onClose, onCreated }) => {
  const isNew = taskId === 'new';
  const q = useTaskQuery(isNew ? undefined : taskId);
  const create = useCreateTaskMutation();
  const update = useUpdateTaskMutation();
  const star = useStarTaskMutation();
  const sync = useGithubSyncMutation();
  const task = q.data;
  const me = meta?.me ?? null;
  const people = meta?.people;

  const base = useMemo(() => (task ? fromTask(task) : null), [task]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [editingBody, setEditingBody] = useState(isNew);

  // Reset the draft when switching tasks or when the saved task changes underneath.
  useEffect(() => {
    if (isNew) setDraft(blank(defaults ?? {}, me));
    else if (base) setDraft(base);
    setError('');
    setEditingBody(isNew);
    setExpanded(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, base]);

  const changes = draft ? diff(isNew ? null : base, draft) : {};
  const dirty = isNew ? !!draft?.title.trim() : Object.keys(changes).length > 0;
  const saving = create.isPending || update.isPending;

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => (d ? { ...d, [k]: v } : d));

  const save = async () => {
    if (!draft) return;
    if (!draft.title.trim()) return setError('Title is required.');
    setError('');
    try {
      if (isNew) {
        const created = await create.mutateAsync(diff(null, draft));
        onCreated(created.id);
      } else if (dirty) {
        await update.mutateAsync({ id: taskId, ...changes });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the task.');
    }
  };

  const cancel = () => {
    if (isNew) onClose();
    else if (base) setDraft(base);
    setEditingBody(false);
    setError('');
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') void save();
      if (e.key === 'Escape' && !dirty) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!isNew && q.isError) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-sm text-gray-500">
        This task could not be loaded.
        <Button size="sm" variant="outline" onClick={onClose}>Close</Button>
      </div>
    );
  }
  if (!draft || (!isNew && !task)) {
    return (
      <div className="grid gap-3 p-4">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-6 w-2/3" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  const projects = meta?.projects ?? [];
  const project = projects.find((p) => p.id === draft.projectId);
  const closed = isClosed(draft.status);
  const link = task ? `${window.location.origin}/admin/tasks/${task.id}` : '';

  return (
    <div className="flex h-full min-h-0 flex-col bg-white">
      {/* Header bar */}
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2">
        <button
          type="button"
          role="switch"
          aria-checked={!closed}
          onClick={() => set('status', closed ? 'open' : 'done')}
          className={`flex h-7 items-center rounded border text-xs font-semibold ${closed ? 'border-gray-300 bg-white text-gray-600' : 'border-emerald-600 bg-emerald-500 text-white'}`}
        >
          <span className="px-2">{closed ? 'Closed' : 'Open'}</span>
          <span className={`mr-0.5 h-5 w-5 rounded-sm ${closed ? 'bg-gray-200' : 'bg-white'}`} />
        </button>
        <div className="flex items-center gap-1.5" aria-label="Owner">
          <Avatar people={people} uid={draft.ownerUid} size={26} />
          <SearchableSelect
            className="w-44"
            placeholder="Owner"
            value={draft.ownerUid == null ? '' : String(draft.ownerUid)}
            onChange={(v) => set('ownerUid', v ? Number(v) : null)}
            options={personOptions(
              people,
              draft.ownerUid != null && !people?.some((p) => p.uid === draft.ownerUid)
                ? [{ value: '', label: 'Unowned' }, { value: String(draft.ownerUid), label: personName(people, draft.ownerUid) }]
                : [{ value: '', label: 'Unowned' }],
            )}
          />
        </div>
        {me != null && draft.ownerUid !== me && (
          <button type="button" onClick={() => set('ownerUid', me)} className="text-xs text-blue-600 hover:underline">
            Claim
          </button>
        )}
        <select
          aria-label="Priority"
          value={draft.priority}
          onChange={(e) => set('priority', e.target.value as TaskPriority)}
          className={`h-7 rounded px-1.5 text-xs font-semibold outline-none ${draft.priority === 'none' ? 'border border-gray-200 bg-white text-gray-500' : PRIORITY[draft.priority].cls}`}
        >
          {PRIORITY_ORDER.map((p) => <option key={p} value={p} className="bg-white text-gray-900">{p === 'none' ? 'No Pri' : `${PRIORITY[p].short} Pri`}</option>)}
        </select>
        <select
          aria-label="Status"
          value={draft.status}
          onChange={(e) => set('status', e.target.value as TaskStatus)}
          className={`h-7 rounded px-1.5 text-xs font-semibold outline-none ${draft.status === 'open' ? 'border border-gray-200 bg-white text-gray-500' : STATUS[draft.status].cls}`}
        >
          {STATUS_ORDER.map((s) => <option key={s} value={s} className="bg-white text-gray-900">{STATUS[s].label}</option>)}
        </select>
        <div className="ml-auto flex items-center gap-2">
          {(dirty || isNew) && <Button size="sm" variant="ghost" onClick={cancel}>Cancel</Button>}
          <Button size="sm" onClick={() => void save()} disabled={!dirty || saving}>
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : isNew ? 'Create' : 'Save'}
          </Button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {/* Number, title, actions */}
        <div className="px-4 pt-3">
          <div className="flex items-center gap-2">
            {task ? (
              <button
                type="button"
                className="text-sm font-semibold text-blue-600 hover:underline"
                title="Copy link"
                onClick={() => void navigator.clipboard?.writeText(link)}
              >
                T{task.number} <span className="text-xs font-normal text-gray-400">· copy</span>
              </button>
            ) : (
              <span className="text-sm font-semibold text-gray-500">New task</span>
            )}
            <div className="ml-auto flex items-center gap-1">
              {task && (
                <button
                  type="button"
                  aria-label={task.starred ? 'Unstar' : 'Star'}
                  title={task.starred ? 'Remove from My To Do List' : 'Add to My To Do List'}
                  onClick={() => star.mutate({ id: task.id, starred: !task.starred })}
                  className="rounded p-1 hover:bg-gray-100"
                >
                  <Star className={`h-4 w-4 ${task.starred ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400'}`} />
                </button>
              )}
              {task && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button type="button" aria-label="More" className="rounded p-1 hover:bg-gray-100">
                      <MoreHorizontal className="h-4 w-4 text-gray-500" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={() => void navigator.clipboard?.writeText(link)}>
                      <Link2 className="mr-2 h-3.5 w-3.5" /> Copy link
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => window.open(link, '_blank')}>
                      <ExternalLink className="mr-2 h-3.5 w-3.5" /> Open in new tab
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              <button type="button" aria-label="Close" onClick={onClose} className="rounded p-1 hover:bg-gray-100">
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
          </div>
          <input
            aria-label="Title"
            autoFocus={isNew}
            value={draft.title}
            onChange={(e) => set('title', e.target.value)}
            placeholder="Task title"
            className={`mt-1 w-full rounded border border-transparent px-1 py-0.5 text-lg font-semibold outline-none hover:border-gray-200 focus:border-blue-400 ${closed ? 'text-gray-500 line-through' : 'text-gray-900'}`}
          />
          {editingBody ? (
            <textarea
              aria-label="Description"
              rows={6}
              value={draft.body}
              onChange={(e) => set('body', e.target.value)}
              placeholder="Add a description"
              className={`${TEXTAREA_CLASS} mt-2`}
            />
          ) : (
            <div
              role="button"
              tabIndex={0}
              onClick={() => setEditingBody(true)}
              onKeyDown={(e) => e.key === 'Enter' && setEditingBody(true)}
              className="mt-2 cursor-text rounded px-1 py-1 text-sm text-gray-800 hover:bg-gray-50"
            >
              {draft.body ? (
                <>
                  <p className={`whitespace-pre-wrap ${expanded ? '' : 'line-clamp-5'}`}>{draft.body}</p>
                  {draft.body.split('\n').length > 5 || draft.body.length > 400 ? (
                    <button
                      type="button"
                      className="text-blue-600 hover:underline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpanded((x) => !x);
                      }}
                    >
                      {expanded ? 'See Less' : 'See More'}
                    </button>
                  ) : null}
                </>
              ) : (
                <span className="text-gray-400">Add a description</span>
              )}
            </div>
          )}
          {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        </div>

        {/* Fields */}
        <div className="mt-3 border-t border-gray-100">
          <Row icon={<Users className="h-3.5 w-3.5" />} label="Subscribers">
            <div className="flex flex-wrap items-center gap-1">
              {draft.subscribers.map((uid) => (
                <Chip key={uid} onRemove={() => set('subscribers', draft.subscribers.filter((u) => u !== uid))}>
                  {personName(people, uid)}
                </Chip>
              ))}
              <SearchableSelect
                className="w-48"
                placeholder="+ Add subscriber"
                value=""
                onChange={(v) => v && set('subscribers', [...draft.subscribers, Number(v)])}
                options={personOptions(people?.filter((p) => !draft.subscribers.includes(p.uid)))}
              />
            </div>
          </Row>
          <Row icon={<FolderKanban className="h-3.5 w-3.5" />} label="Project">
            <div className="flex flex-wrap gap-2">
              <select
                aria-label="Project"
                value={draft.projectId ?? ''}
                onChange={(e) => setDraft((d) => (d ? { ...d, projectId: e.target.value || null, sectionId: null } : d))}
                className={SEL}
              >
                <option value="">No project</option>
                {projects.filter((p) => !p.archived || p.id === draft.projectId).map((p) => (
                  <option key={p.id} value={p.id}>{p.parentId ? `  ${p.name}` : p.name}</option>
                ))}
              </select>
              {project && project.sections.length > 0 && (
                <select aria-label="Section" value={draft.sectionId ?? ''} onChange={(e) => set('sectionId', e.target.value || null)} className={SEL}>
                  <option value="">No section</option>
                  {project.sections.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              )}
            </div>
          </Row>
          <Row icon={<Tag className="h-3.5 w-3.5" />} label="Tags">
            <div className="flex flex-wrap items-center gap-1">
              {draft.tags.map((t) => (
                <Chip key={t} onRemove={() => set('tags', draft.tags.filter((x) => x !== t))}>{t}</Chip>
              ))}
              <ChipInput
                ariaLabel="Add tag"
                placeholder="Add a tag"
                onAdd={(v) => {
                  const t = v.replace(/^#/, '').toLowerCase().replace(/\s+/g, '-');
                  if (t && !draft.tags.includes(t)) set('tags', [...draft.tags, t]);
                }}
              />
            </div>
          </Row>
          <Row icon={<CalendarDays className="h-3.5 w-3.5" />} label="Schedule">
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-1 text-gray-500">
                Start
                <input type="date" aria-label="Start date" value={draft.startDate} onChange={(e) => set('startDate', e.target.value)} className={SEL} />
              </label>
              <label className="flex items-center gap-1 text-gray-500">
                Target
                <input type="date" aria-label="Target date" value={draft.dueDate} onChange={(e) => set('dueDate', e.target.value)} className={SEL} />
              </label>
              <label className="flex items-center gap-1 text-xs text-gray-600">
                <input type="checkbox" checked={draft.isMilestone} onChange={(e) => set('isMilestone', e.target.checked)} />
                Milestone
              </label>
            </div>
          </Row>
          <Row icon={<BarChart3 className="h-3.5 w-3.5" />} label="Progress">
            <div className="flex flex-wrap items-center gap-3" role="radiogroup" aria-label="Progress">
              {(meta?.progressSteps ?? [0, 25, 50, 75, 100]).filter((p) => p > 0).map((p) => (
                <label key={p} className="flex items-center gap-1 text-gray-700">
                  <input type="radio" name="progress" checked={draft.progress === p} onChange={() => set('progress', p)} />
                  {p}%
                </label>
              ))}
              {draft.progress != null && (
                <button type="button" className="text-xs text-gray-400 hover:text-gray-600" onClick={() => set('progress', null)}>
                  Clear
                </button>
              )}
            </div>
          </Row>
          <Row icon={<Clock className="h-3.5 w-3.5" />} label="Estimate">
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={0}
                step="0.5"
                aria-label="Estimate in hours"
                value={draft.estimateHours}
                onChange={(e) => set('estimateHours', e.target.value)}
                placeholder="Enter #"
                className={`${SEL} w-24`}
              />
              <span className="text-gray-500">Hours</span>
            </div>
          </Row>
          <Row icon={<Lock className="h-3.5 w-3.5" />} label="Blocked by">
            <div className="flex flex-wrap items-center gap-1">
              {draft.dependsOn.map((ref) => {
                const known = task?.blockedBy.find((b) => `T${b.number}` === ref);
                return (
                  <Chip key={ref} title={known?.title} onRemove={() => set('dependsOn', draft.dependsOn.filter((x) => x !== ref))}>
                    <span className={known && isClosed(known.status) ? 'line-through' : ''}>
                      {ref}{known ? ` ${known.title}` : ''}
                    </span>
                  </Chip>
                );
              })}
              <ChipInput
                ariaLabel="Add blocker by task number"
                placeholder="T123"
                onAdd={(v) => {
                  const ref = `T${v.replace(/^[Tt]/, '')}`;
                  if (/^T\d+$/.test(ref) && !draft.dependsOn.includes(ref)) set('dependsOn', [...draft.dependsOn, ref]);
                }}
              />
            </div>
            {task && task.blocking.length > 0 && (
              <p className="mt-1 text-xs text-gray-500">
                Blocking: {task.blocking.map((b) => `T${b.number} ${b.title}`).join(', ')}
              </p>
            )}
          </Row>
          <Row icon={<Github className="h-3.5 w-3.5" />} label="GitHub">
            <div className="grid gap-1">
              {draft.githubIssues.map((ref) => {
                const issue = task?.githubIssues.find((i) => i.ref === ref);
                return (
                  <div key={ref} className="flex items-center gap-2 text-xs">
                    <span
                      className={`rounded-full px-1.5 py-0.5 font-semibold ${issue?.state === 'closed' ? 'bg-violet-100 text-violet-700' : issue?.state === 'open' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}
                    >
                      {issue?.state ?? (issue?.error ? 'error' : 'linked')}
                    </span>
                    <a
                      href={issue?.url ?? `https://github.com/${ref.replace('#', '/issues/')}`}
                      target="_blank"
                      rel="noreferrer"
                      className="min-w-0 truncate text-blue-600 hover:underline"
                    >
                      {ref}{issue?.title ? ` ${issue.title}` : ''}
                    </a>
                    <button type="button" aria-label={`Unlink ${ref}`} onClick={() => set('githubIssues', draft.githubIssues.filter((x) => x !== ref))} className="text-gray-400 hover:text-gray-700">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                );
              })}
              <div className="flex items-center gap-2">
                <ChipInput
                  ariaLabel="Link a GitHub issue"
                  placeholder="ff-api#123 or issue URL"
                  onAdd={(v) => !draft.githubIssues.includes(v) && set('githubIssues', [...draft.githubIssues, v])}
                />
                {task && task.githubIssues.length > 0 && (
                  <button
                    type="button"
                    onClick={() => sync.mutate()}
                    disabled={sync.isPending}
                    className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800"
                    title="Refresh issue states; closes this task once every linked issue is closed"
                  >
                    <RefreshCw className={`h-3 w-3 ${sync.isPending ? 'animate-spin' : ''}`} /> Sync
                  </button>
                )}
              </div>
              {sync.data?.errors?.length ? <p className="text-xs text-amber-700">{sync.data.errors.join('; ')}</p> : null}
              {sync.isError && <p className="text-xs text-red-600">{sync.error instanceof Error ? sync.error.message : 'Sync failed'}</p>}
            </div>
          </Row>
          {task && (
            <Row icon={<Users className="h-3.5 w-3.5" />} label="Creator">
              <span className="text-blue-700">{task.createdBy ? personName(people, task.createdBy) : `System (${task.source})`}</span>
              <span className="ml-2 text-xs text-gray-400">{fmtUpdated(task.createdAt)}</span>
              {task.source !== 'manual' && (
                <p className="text-xs text-gray-500">
                  Filed by {task.source}{task.sourceRef ? ` (${task.sourceRef})` : ''}
                  {task.hits > 1 ? ` · seen ${task.hits} times, last ${fmtUpdated(task.lastSeenAt)}` : ''}
                </p>
              )}
            </Row>
          )}
        </div>

        {task && <Activity task={task} meta={meta} />}
      </div>

      {task && <CommentBox taskId={task.id} me={me} people={people} />}
    </div>
  );
};

/** "Activity & comments", newest last, grouped like Meta (this week / this month / older). */
const Activity: React.FC<{ task: TaskDetail; meta: TaskMeta | undefined }> = ({ task, meta }) => {
  const people = meta?.people;
  const me = meta?.me ?? null;
  const seen = task.seenBy.filter((u) => u != null);
  const bucket = (iso: string) => {
    const days = (Date.now() - new Date(iso).getTime()) / 86400000;
    return days < 7 ? 'Within the last week' : days < 31 ? 'Within the last month' : 'Older';
  };
  let last = '';
  return (
    <div className="px-4 py-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Activity &amp; comments</h3>
      {seen.length > 0 && (
        <>
          <Divider label="Latest update seen by" />
          <p className="px-2 text-xs text-blue-700">
            {seen.map((u) => (u === me ? 'You' : personName(people, u))).join(', ').replace(/, ([^,]*)$/, ' and $1')}
          </p>
        </>
      )}
      {task.comments.length === 0 && <p className="mt-3 text-sm text-gray-400">No activity yet.</p>}
      <ol className="mt-1 grid gap-2">
        {[...task.comments].reverse().map((c) => {
          const b = bucket(c.createdAt);
          const header = b !== last ? b : '';
          last = b;
          return (
            <li key={c.id}>
              {header && <Divider label={header} />}
              {c.kind === 'event' ? <EventLine c={c} people={people} /> : <CommentCard taskId={task.id} c={c} me={me} people={people} />}
            </li>
          );
        })}
      </ol>
    </div>
  );
};

const Divider: React.FC<{ label: string }> = ({ label }) => (
  <div className="my-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
    <span className="h-px w-3 bg-gray-200" />
    {label}
    <span className="h-px flex-1 bg-gray-200" />
  </div>
);

const EventLine: React.FC<{ c: TaskComment; people: TaskMeta['people'] | undefined }> = ({ c, people }) => (
  <div className="flex gap-2 px-2 text-xs text-gray-500">
    <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300" />
    <span>
      <span className="font-medium text-gray-700">{c.authorUid == null ? 'System' : personName(people, c.authorUid)}</span> {c.body}.
      <span className="ml-1 text-gray-400">{fmtUpdated(c.createdAt)}</span>
    </span>
  </div>
);

const CommentCard: React.FC<{ taskId: string; c: TaskComment; me: number | null; people: TaskMeta['people'] | undefined }> = ({
  taskId,
  c,
  me,
  people,
}) => {
  const like = useLikeCommentMutation();
  const edit = useEditCommentMutation();
  const del = useDeleteCommentMutation();
  const [editing, setEditing] = useState<string | null>(null);
  const mine = me != null && c.authorUid === me;
  return (
    <div className="flex gap-2 rounded-md border border-gray-100 bg-white p-2 shadow-sm">
      <Avatar people={people} uid={c.authorUid} size={28} />
      <div className="min-w-0 flex-1 text-sm">
        {editing !== null ? (
          <div className="grid gap-1">
            <textarea aria-label="Edit comment" rows={3} value={editing} onChange={(e) => setEditing(e.target.value)} className={TEXTAREA_CLASS} />
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={!editing.trim() || edit.isPending}
                onClick={async () => {
                  await edit.mutateAsync({ id: taskId, commentId: c.id, body: editing });
                  setEditing(null);
                }}
              >
                Save
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <p className="whitespace-pre-wrap text-gray-800">
            <span className="mr-1 font-semibold text-blue-700">{personName(people, c.authorUid)}</span>
            {c.body}
          </p>
        )}
        <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-gray-400">
          <span>{fmtUpdated(c.createdAt)}{c.editedAt ? ' · edited' : ''}</span>
          <button type="button" className={`hover:underline ${c.likedByMe ? 'font-semibold text-blue-600' : 'text-blue-600'}`} onClick={() => like.mutate({ id: taskId, commentId: c.id, liked: !c.likedByMe })}>
            {c.likedByMe ? 'Unlike' : 'Like'}
          </button>
          {c.likes.length > 0 && <span title={c.likes.map((u) => personName(people, u)).join(', ')}>👍 {c.likes.length}</span>}
          {mine && editing === null && (
            <>
              <button type="button" className="text-blue-600 hover:underline" onClick={() => setEditing(c.body)}>Edit</button>
              <button
                type="button"
                className="text-blue-600 hover:underline"
                onClick={() => window.confirm('Delete this comment?') && del.mutate({ id: taskId, commentId: c.id })}
              >
                Delete
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const CommentBox: React.FC<{ taskId: string; me: number | null; people: TaskMeta['people'] | undefined }> = ({ taskId, me, people }) => {
  const comment = useCommentTaskMutation();
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const post = async () => {
    if (!text.trim()) return;
    setError('');
    try {
      await comment.mutateAsync({ id: taskId, body: text.trim() });
      setText('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not post the comment.');
    }
  };
  return (
    <div className="border-t border-gray-200 bg-gray-50 p-2">
      <div className="flex items-end gap-2">
        <Avatar people={people} uid={me} size={28} />
        <textarea
          aria-label="Write a comment"
          rows={1}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void post();
            }
          }}
          placeholder="Write a comment…"
          className={`${TEXTAREA_CLASS} max-h-32 min-h-[34px] resize-none`}
        />
        <Button size="sm" onClick={() => void post()} disabled={!text.trim() || comment.isPending}>Save</Button>
      </div>
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
};

export default TaskPanel;
